import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useClientTranslation } from '@/lib/core';
import { SpinLoading } from '@/components/shared/loadings/spin';

type RequestStatus = 'idle' | 'loading' | 'error';

export interface BookingRequestSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSeeAvailableSlots: () => void;
  onRequestAnyway: () => Promise<void>;
  onSuccess?: () => void;
  headerTitle?: string;
  title?: string;
  description?: string;
  seeAvailableSlotsLabel?: string;
  requestAnywayLabel?: string;
}

export function BookingRequestSheet({
  open,
  onOpenChange,
  onSeeAvailableSlots,
  onRequestAnyway,
  onSuccess,
  headerTitle,
  title,
  description,
  seeAvailableSlotsLabel,
  requestAnywayLabel,
}: BookingRequestSheetProps) {
  const t = useClientTranslation();
  const [status, setStatus] = useState<RequestStatus>('idle');

  useEffect(() => {
    if (open) return;
    const timer = setTimeout(() => setStatus('idle'), 300);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (status !== 'error') return;
    const timer = setTimeout(() => setStatus('idle'), 1500);
    return () => clearTimeout(timer);
  }, [status]);

  async function handleRequestAnyway() {
    setStatus('loading');
    try {
      await onRequestAnyway();
      onSuccess?.();
    } catch {
      setStatus('error');
    }
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} showSwipeHandle={false}>
      <DrawerContent className="bg-[#1a1a1a]">
        {status === 'loading' ? (
          <SpinLoading wrapperClassName="min-h-0 flex-1 my-8" spinnerClassName="border-white" />
        ) : status === 'error' ? (
          <div className="animate-sheet-result-in flex items-center gap-2 px-4 py-8">
            <Icon name="warning-circle" width={16} height={16} className="shrink-0 text-white" />
            <p className="font-pp-neue-montreal text-[14px] tracking-[0.5px] text-white">
              {t('App.Labels.BookingError')}
            </p>
          </div>
        ) : (
          <>
            <DrawerHeader className="shrink-0 px-4 pt-4">
              <p className="font-pp-neue-montreal text-center text-[14px] tracking-[0.5px] text-white uppercase">
                {headerTitle ?? t('App.Labels.Confirmation')}
              </p>
            </DrawerHeader>

            <div className="flex flex-1 flex-col gap-4 px-4 py-6">
              <DrawerTitle className="font-pp-neue-montreal text-[24px] leading-tight font-semibold text-white">
                {title ?? t('App.Labels.SlotFullyBooked')}
              </DrawerTitle>
              <DrawerDescription className="font-pp-neue-montreal text-[16px] leading-normal tracking-[0.25px] text-[#eaeaea]">
                {description ?? t('App.Labels.SlotFullyBookedDescription')}
              </DrawerDescription>
            </div>

            <DrawerFooter className="flex shrink-0 flex-col gap-2 border-t border-[#c8c8c8]/30 px-4 pt-6 pb-10">
              <Button
                variant="primary-solid"
                palette="white"
                size="lg"
                className="w-full"
                onClick={onSeeAvailableSlots}
              >
                {seeAvailableSlotsLabel ?? t('App.Labels.SeeAvailableSlots')}
              </Button>
              <Button
                variant="tertiary-link"
                palette="white"
                size="sm"
                className="w-full"
                onClick={handleRequestAnyway}
              >
                {requestAnywayLabel ?? t('App.Labels.RequestAnyway')}
              </Button>
            </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
}
